// The amounts a donor gives each recipient on a multi-recipient form.
// The rows sum into the gift total; the total is never stored on its own.
//
// Rules:
// - Changing one row's amount moves the total by the same amount; no other row moves.
// - Removing a row sets its amount to zero, the same state as typing zero. Each row remembers
//   the last amount above zero it had, and restoring a row puts that amount back.
// - An ask preset or a total typed into Other splits evenly across the funded rows. A removed
//   row keeps its remembered amount.
// - A locked row is a recipient whose gift already went through. It shows the amount that went
//   through, posts nothing, and counts toward neither the total nor the minimum.
//
// Amounts are kept in whole cents so that sums never drift.

function toCents(dollars) {
  var value = parseFloat(dollars);
  if (!isFinite(value) || value <= 0) {
    return 0;
  }
  return Math.round(value * 100);
}

function toDollars(cents) {
  return cents / 100;
}

// Splits `cents` into `count` whole-cent shares. The remainder goes one cent at a time to the
// first shares, the same order the server's SplitCurrencyAmount uses.
function splitCents(cents, count) {
  var low = Math.floor(cents / count);
  var highs = cents - low * count;
  var shares = [];
  for (var i = 0; i < count; i++) {
    shares.push(i < highs ? low + 1 : low);
  }
  return shares;
}

export default class RecipientAllocation {
  // `beneficiaries` is the definition's `beneficiaries` block. Rows follow its `displayOrder`, which
  // also decides who gets the remainder cents; entries without one keep their place at the end.
  constructor(beneficiaries, startingTotal) {
    var ordered = (beneficiaries || []).map(function (b, index) {
      return { b: b, index: index, order: isFinite(parseFloat(b.displayOrder)) ? parseFloat(b.displayOrder) : Infinity };
    }).sort(function (x, y) {
      return x.order - y.order || x.index - y.index;
    });
    this.rows = ordered.map(function (entry) {
      var b = entry.b;
      return {
        id: b.gatewayAccountId,
        displayName: b.displayName || '',
        descriptor: b.descriptor || '',
        imageUrl: b.imageUrl || null,
        cents: 0,
        rememberedCents: 0,
        locked: false,
        // A recipient the server says can't be retried (see applyLegs): removed, and can't be restored
        closed: false,
        message: null
      };
    });
    // Every recipient starts funded at an even share.
    this.splitEvenlyAcross(this.rows, toCents(startingTotal));
  }

  row(id) {
    return this.rows.find(function (r) {
      return r.id === id;
    }) || null;
  }

  isFunded(row) {
    return !row.locked && row.cents > 0;
  }

  isRemoved(row) {
    return !row.locked && row.cents === 0;
  }

  fundedRows() {
    return this.rows.filter(this.isFunded, this);
  }

  fundedCount() {
    return this.fundedRows().length;
  }

  totalCents() {
    return this.fundedRows().reduce(function (sum, r) {
      return sum + r.cents;
    }, 0);
  }

  total() {
    return toDollars(this.totalCents());
  }

  amount(id) {
    var row = this.row(id);
    return row ? toDollars(row.cents) : 0;
  }

  // The smallest total the form accepts: the larger of the form's minimum and one cent per
  // funded recipient. No recipient has a minimum of its own.
  minimum(valueMin) {
    var formMin = parseFloat(valueMin) || 0;
    return Math.max(formMin, toDollars(this.fundedCount()));
  }

  // True when every funded row holds the same amount, give or take the remainder cents.
  isEvenSplit() {
    var funded = this.fundedRows();
    if (funded.length < 2) {
      return true;
    }
    var amounts = funded.map(function (r) {
      return r.cents;
    });
    return Math.max.apply(null, amounts) - Math.min.apply(null, amounts) <= 1;
  }

  setAmount(id, dollars) {
    var row = this.row(id);
    if (!row || row.locked || row.closed) {
      return;
    }
    this.setRowCents(row, toCents(dollars));
  }

  // Adds or takes away one whole dollar and keeps the cents, so $3.33 steps to $4.33.
  // Stepping below zero lands on zero, which removes the row.
  step(id, direction) {
    var row = this.row(id);
    if (!row || row.locked || row.closed) {
      return;
    }
    this.setRowCents(row, Math.max(0, row.cents + (direction < 0 ? -100 : 100)));
  }

  remove(id) {
    var row = this.row(id);
    if (row && !row.locked) {
      row.cents = 0;
    }
  }

  restore(id) {
    var row = this.row(id);
    if (row && this.isRemoved(row) && !row.closed) {
      // A row that has never held an amount restores to one cent, the smallest share it can take.
      row.cents = row.rememberedCents || 1;
    }
  }

  // Remove all and restore all act on the rows the donor can see, which is the current search.
  removeAll(ids) {
    ids.forEach(this.remove, this);
  }

  restoreAll(ids) {
    ids.forEach(this.restore, this);
  }

  // An ask preset or a total typed into Other. Splits across the funded rows. When nothing is
  // funded, there's no row to split across, so it splits across every row that isn't locked.
  splitEvenly(totalDollars) {
    var targets = this.fundedRows();
    if (!targets.length) {
      targets = this.rows.filter(function (r) {
        return !r.locked && !r.closed;
      });
    }
    this.splitEvenlyAcross(targets, toCents(totalDollars));
  }

  splitEvenlyAcross(targets, cents) {
    if (!targets.length) {
      return;
    }
    var shares = splitCents(cents, targets.length);
    targets.forEach(function (row, i) {
      this.setRowCents(row, shares[i]);
    }, this);
  }

  setRowCents(row, cents) {
    row.cents = cents;
    if (cents > 0) {
      row.rememberedCents = cents;
    }
  }

  // The post body: `{ "<gatewayAccountId>": { am, ccAm } }` for each funded row. Removed and
  // locked rows are left out. The cover-costs amount splits in proportion to each row's share,
  // with remainder cents going to the first rows.
  toSplitAmounts(coverCostDollars) {
    var funded = this.fundedRows();
    var total = this.totalCents();
    var coverCents = toCents(coverCostDollars);
    var coverShares = funded.map(function (r) {
      return total > 0 ? Math.floor(coverCents * r.cents / total) : 0;
    });
    var leftover = coverCents - coverShares.reduce(function (a, b) {
      return a + b;
    }, 0);
    for (var i = 0; leftover > 0 && funded.length; i = (i + 1) % funded.length) {
      coverShares[i]++;
      leftover--;
    }

    var splits = {};
    funded.forEach(function (r, i) {
      splits[r.id] = { am: toDollars(r.cents), ccAm: toDollars(coverShares[i]) };
    });
    return splits;
  }

  // The return to the form after some recipients failed. Every row starts at the amount the
  // response carries for it. A row that isn't preselected starts removed, so restoring it is the
  // ordinary restore to that amount. A row that went through is locked at the amount it was given.
  applyLegs(legs) {
    (legs || []).forEach(function (leg) {
      var row = this.row(leg.gatewayAccountId);
      if (!row) {
        return;
      }
      row.locked = false;
      row.closed = false;
      this.setRowCents(row, toCents(leg.amount));
      row.message = leg.donorMessage || null;
      if (leg.outcome === 'Succeeded') {
        row.locked = true;
      } else if (leg.canRetry === false) {
        // Reversed, or a reversal that failed after the charge landed: charging again could charge twice
        row.closed = true;
        row.cents = 0;
      } else if (!leg.preselected) {
        row.cents = 0;
      }
    }, this);
  }
}
